import classNames from "classnames";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { HeadCellContent } from "components/DesignSystem/HeadCellContent/HeadCellContent";
import Table from "components/DesignSystem/Table/V2/Table";
import { CaretDown } from "components/icons/CaretDown";
import {
  AddNewClass,
  AddNewGroup,
} from "components/Transaction/Slider/Classifications";
import { DISPLAY_DATE } from "constants/date";
import { CLASSIFICATION_COLUMN_IDS } from "constants/classification";
import dayjs from "dayjs";
import { useModal } from "hooks/useModal";
import { useMemo, useState } from "react";
import {
  createColumnHelper,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  Row,
  SortingState,
  useReactTable,
} from "react-table-8.10.7";
import { useGetAllClassificationQuery } from "store/apis/classifications";
import { Class, ClassificationGroup } from "types/Models/classification";
import ThreeDots from "static/images/ThreeDots.svg";
import { AnimatePresence, motion } from "framer-motion";
import { EditGroupModal } from "./ClassificationModals/EditGroupModal";
import { EditClassificationModal } from "./ClassificationModals/EditClassificationModal";
import { DeleteClassificationModal } from "./ClassificationModals/DeleteClassificationModal";
import { DeleteGroupModal } from "./ClassificationModals/DeleteGroupModal";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import ArrowElbowDownRight from "static/images/ArrowElbowDownRight.svg";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";
import { LockedUi } from "components/LockedUi/LockedUi";
import { useWindowSize } from "hooks/useWindowSize";
import { MobileClassificationRow } from "./MobileClassificationRow";

export type ClassificationTableRow = {
  uuid: string;
  name: string;
  created_at: string;
  created_by: string;
  isGroup: boolean;
  groupUuid?: string;
  groupName?: string;
  children?: ClassificationTableRow[];
};

const buildTableData = (
  groups: ClassificationGroup[]
): ClassificationTableRow[] =>
  groups.map((group) => ({
    uuid: group.uuid,
    name: group.name,
    created_at: group.created_at,
    created_by: group.created_by,
    isGroup: true,
    children: group.classes.map((cls) => ({
      uuid: cls.uuid,
      name: cls.name,
      created_at: cls.created_at,
      created_by: cls.created_by,
      isGroup: false,
      groupUuid: group.uuid,
      groupName: group.name,
    })),
  }));

const columnHelper = createColumnHelper<ClassificationTableRow>();

const columns = [
  columnHelper.display({
    id: CLASSIFICATION_COLUMN_IDS.ICON,
    size: 3,
    minSize: 3,
    header: "",
    cell: ({ row }) => {
      if (!row.original.isGroup) return null;

      return (
        <div
          className={classNames(
            "t-text-neutral t-transform t-transition t-duration-300 t-ease-in-out t-flex-shrink-0",
            {
              "-t-rotate-0": row.getIsExpanded(),
              "-t-rotate-90": !row.getIsExpanded(),
            }
          )}
        >
          <CaretDown />
        </div>
      );
    },
  }),

  columnHelper.accessor("name", {
    id: CLASSIFICATION_COLUMN_IDS.GROUP_CLASS,
    size: 62,
    header: (header) => (
      <HeadCellContent
        title="Group/Class"
        align="left"
        sort={{
          nextSortOrder: header.column.getNextSortingOrder(),
          isSorted: header.column.getIsSorted(),
          sortingHandler: header.column.getToggleSortingHandler(),
        }}
      />
    ),
    cell: ({ row, getValue }) => {
      const name = getValue();
      const isGroup = row.original.isGroup;

      return (
        <div className="t-flex t-items-center t-gap-2">
          {!isGroup && (
            // <span className="t-text-neutral-40 t-text-body-sm">└</span>
            <img src={ArrowElbowDownRight} alt="ArrowElbowDownRight" />
          )}
          <span className="t-text-body-sm t-text-text-60">{name}</span>
        </div>
      );
    },
  }),

  columnHelper.accessor("created_at", {
    id: CLASSIFICATION_COLUMN_IDS.CREATED_ON,
    size: 15,
    header: (header) => (
      <HeadCellContent
        title="Created on"
        align="left"
        sort={{
          nextSortOrder: header.column.getNextSortingOrder(),
          isSorted: header.column.getIsSorted(),
          sortingHandler: header.column.getToggleSortingHandler(),
        }}
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      if (!value) return <span className="t-text-text-30">-</span>;

      return (
        <span className="t-text-body-sm t-text-text-60">
          {dayjs(value).format(DISPLAY_DATE)}
        </span>
      );
    },
  }),

  columnHelper.accessor("created_by", {
    id: CLASSIFICATION_COLUMN_IDS.CREATED_BY,
    size: 15,
    header: (header) => (
      <HeadCellContent
        title="Created by"
        align="left"
        sort={{
          nextSortOrder: header.column.getNextSortingOrder(),
          isSorted: header.column.getIsSorted(),
          sortingHandler: header.column.getToggleSortingHandler(),
        }}
      />
    ),
    cell: (info) => {
      const value = info.getValue();

      return (
        <span className="t-text-body-sm t-text-text-60">{value || "-"}</span>
      );
    },
  }),

  columnHelper.display({
    id: CLASSIFICATION_COLUMN_IDS.ACTIONS,
    size: 5,
    header: "",
    cell: (info) => {
      return <ClassificationActions row={info.row} />;
    },
  }),
];

const ClassificationActions = ({
  row,
}: {
  row: Row<ClassificationTableRow>;
}) => {
  const isGroup = row.original.isGroup;
  const entityId = useCurrentEntityId();
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.TRANSACTIONS
  );

  const addClassModal = useModal();
  const editGroupModal = useModal();
  const editClassModal = useModal();
  const deleteGroupModal = useModal();
  const deleteClassModal = useModal();

  const [selectedRow, setSelectedRow] = useState<ClassificationTableRow | null>(
    null
  );
  const [addClassToGroup, setAddClassToGroup] = useState<string | null>(null);

  if (!hasWritePermission) return null;

  const handleEditGroup = (row: ClassificationTableRow) => {
    setSelectedRow(row);
    editGroupModal.open();
  };

  const handleAddClassification = (groupName: string) => {
    setAddClassToGroup(groupName);
    addClassModal.open();
  };

  const handleDeleteGroup = (row: ClassificationTableRow) => {
    setSelectedRow(row);
    deleteGroupModal.open();
  };

  const handleEditClassification = (row: ClassificationTableRow) => {
    setSelectedRow(row);
    editClassModal.open();
  };

  const handleDeleteClassification = (row: ClassificationTableRow) => {
    setSelectedRow(row);
    deleteClassModal.open();
  };

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Dropdown.Root>
        <Dropdown.Trigger asChild>
          <Button type="button" customType="ghost_icon">
            <img src={ThreeDots} alt="Actions" />
          </Button>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content align="end">
            {isGroup ? (
              <>
                <Dropdown.Item onClick={() => handleEditGroup(row.original)}>
                  Edit group
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => handleAddClassification(row.original.name)}
                >
                  Add classification
                </Dropdown.Item>
                <Dropdown.Item
                  type="danger"
                  onClick={() => handleDeleteGroup(row.original)}
                >
                  Delete group
                </Dropdown.Item>
              </>
            ) : (
              <>
                <Dropdown.Item
                  onClick={() => handleEditClassification(row.original)}
                >
                  Edit classification
                </Dropdown.Item>
                <Dropdown.Item
                  type="danger"
                  onClick={() => handleDeleteClassification(row.original)}
                >
                  Delete classification
                </Dropdown.Item>
              </>
            )}
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>

      {addClassModal.isOpen && (
        <AddNewClass
          {...addClassModal}
          onSuccess={(newClass: Class) => {
            addClassModal.close();
          }}
          addClassToGroup={addClassToGroup}
          entityId={entityId}
          showSettingsInfo={false}
        />
      )}

      {editGroupModal.isOpen && selectedRow && (
        <EditGroupModal
          isOpen={editGroupModal.isOpen}
          onClose={editGroupModal.close}
          groupName={selectedRow.name}
          groupId={selectedRow.uuid}
        />
      )}

      {editClassModal.isOpen && selectedRow && (
        <EditClassificationModal
          isOpen={editClassModal.isOpen}
          onClose={editClassModal.close}
          className={selectedRow.name}
          classId={selectedRow.uuid}
          groupName={selectedRow.groupName || ""}
        />
      )}

      {deleteGroupModal.isOpen && selectedRow && (
        <DeleteGroupModal
          isOpen={deleteGroupModal.isOpen}
          onClose={deleteGroupModal.close}
          groupName={selectedRow.name}
          groupId={selectedRow.uuid}
        />
      )}

      {deleteClassModal.isOpen && selectedRow && (
        <DeleteClassificationModal
          isOpen={deleteClassModal.isOpen}
          onClose={deleteClassModal.close}
          classificationName={selectedRow.name}
          classificationId={selectedRow.uuid}
        />
      )}
    </div>
  );
};

const ClassificationTableRows = ({
  row,
}: {
  row: Row<ClassificationTableRow>;
}) => {
  const isGroup = row.original.isGroup;

  return (
    <motion.tr
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.2, ease: "easeInOut" }}
      layout
      className={classNames(
        "t-border t-border-solid t-border-x-0 t-border-t-0 t-border-neutral-0 t-group t-px-3 t-text-body-sm",
        {
          "t-cursor-pointer hover:t-bg-surface-lighter-grey": isGroup,
        }
      )}
      onClick={() => {
        if (isGroup) {
          row.toggleExpanded();
        }
      }}
    >
      {row.getVisibleCells().map((cell) => {
        return (
          <Table.Cell
            key={cell.id}
            style={{
              width: `${cell.column.getSize()}%`,
              maxWidth: `${cell.column.getSize()}%`,
            }}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </Table.Cell>
        );
      })}
    </motion.tr>
  );
};

export const ClassificationsSettings = ({ entityId }: { entityId: string }) => {
  const {
    data: classifications = [],
    isLoading,
    isSuccess,
  } = useGetAllClassificationQuery({ entityId }, { skip: !entityId });

  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [sorting, setSorting] = useState<SortingState>([]);

  const addGroupModal = useModal();

  const tableData = useMemo(
    () => buildTableData(classifications),
    [classifications]
  );

  const isEmpty = tableData.length === 0;
  const { isMobile } = useWindowSize();

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      expanded,
      sorting,
    },
    onExpandedChange: setExpanded,
    onSortingChange: setSorting,
    getSubRows: (row) => row.children,
    getRowId: (row) => row.uuid,
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="t-mt-4 t-pb-20">
      <div className="t-flex t-items-center t-justify-between t-pr-10 t-pb-4 sm:t-pr-0">
        <span className="t-font-medium t-text-text-60 t-text-subtext">
          Classifications
        </span>
        <LockedUi
          feature={WRITE_PERMISSION_KEYS.TRANSACTIONS}
          blockedUi={<LockedButton text="Add Group" />}
        >
          <Button
            customType="primary_outlined"
            size="small"
            onClick={addGroupModal.open}
          >
            + Add Group
          </Button>
        </LockedUi>
      </div>

      <Async.Root isLoading={isLoading} isEmpty={isEmpty} isSuccess={isSuccess}>
        <Async.Empty>
          <div className="t-text-text-60 t-text-body-sm t-text-center t-w-full t-p-8">
            No classifications found. Add a group to get started.
          </div>
        </Async.Empty>
        <Async.Success>
          {isMobile && (
            <div className="t-flex t-flex-col">
              {table.getRowModel().rows.map((row) => (
                <MobileClassificationRow key={row.id} row={row} />
              ))}
            </div>
          )}
          {!isMobile && (
            <Table.Container>
              <Table.Content className="t-w-full">
                <Table.Head>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <Table.HeadRow
                      key={headerGroup.id}
                      className="t-border t-border-solid t-border-x-0 t-border-t-0 t-border-neutral-10 t-bg-surface-lighter-grey"
                    >
                      {headerGroup.headers.map((header) => (
                        <Table.HeadCell
                          key={header.id}
                          isUpperCase={false}
                          className="t-group"
                          style={{
                            width: `${header.getSize()}%`,
                            maxWidth: `${header.getSize()}%`,
                          }}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </Table.HeadCell>
                      ))}
                    </Table.HeadRow>
                  ))}
                </Table.Head>
                <Table.Body>
                  <AnimatePresence initial={false}>
                    {table.getRowModel().rows.map((row) => (
                      <ClassificationTableRows key={row.id} row={row} />
                    ))}
                  </AnimatePresence>
                </Table.Body>
              </Table.Content>
            </Table.Container>
          )}
        </Async.Success>
      </Async.Root>
      {addGroupModal.isOpen && (
        <AddNewGroup
          {...addGroupModal}
          entityId={entityId}
          onSuccess={(newGroup: ClassificationGroup) => {
            addGroupModal.close();
          }}
          showSettingsInfo={false}
        />
      )}
    </div>
  );
};
